/** @format */

import React, { useEffect, useRef } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";

/**
 * The landing sections are marked up with `data-aos` attributes but the AOS
 * library was never installed, so none of them ever animated. This revives the
 * markup with an IntersectionObserver instead of adding a dependency.
 *
 * The hiding styles are scoped to `.ff-reveal-scope`, and that class is only
 * added from inside the effect — so if JS or IntersectionObserver is
 * unavailable the content simply renders visible.
 */
const REVEAL_CSS = {
    ".ff-reveal-scope [data-aos]": {
        opacity: 0,
        willChange: "opacity, transform",
        transition:
            "opacity 0.75s cubic-bezier(0.22,0.61,0.36,1), transform 0.75s cubic-bezier(0.22,0.61,0.36,1)",
    },
    '.ff-reveal-scope [data-aos="fade-up"]': { transform: "translateY(32px)" },
    '.ff-reveal-scope [data-aos="fade-down"]': { transform: "translateY(-32px)" },
    '.ff-reveal-scope [data-aos="fade-left"]': { transform: "translateX(32px)" },
    '.ff-reveal-scope [data-aos="fade-right"]': { transform: "translateX(-32px)" },
    '.ff-reveal-scope [data-aos="zoom-in"]': { transform: "scale(0.92)" },
    ".ff-reveal-scope [data-aos].ff-revealed": {
        opacity: 1,
        transform: "none",
    },
    "@media (prefers-reduced-motion: reduce)": {
        ".ff-reveal-scope [data-aos]": {
            opacity: 1,
            transform: "none",
            transition: "none",
        },
    },
} as const;

const ScrollRevealScope: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const root = rootRef.current;
        if (!root || typeof IntersectionObserver === "undefined") return;

        const prefersReduced =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (prefersReduced) return;

        root.classList.add("ff-reveal-scope");

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const el = entry.target as HTMLElement;
                    const delay = Number(el.dataset.aosDelay ?? 0);
                    if (delay > 0) el.style.transitionDelay = `${delay}ms`;
                    el.classList.add("ff-revealed");
                    observer.unobserve(el);
                });
            },
            { threshold: 0.08, rootMargin: "0px 0px -6% 0px" },
        );

        const observeAll = () => {
            root
                .querySelectorAll<HTMLElement>("[data-aos]:not(.ff-revealed)")
                .forEach((el) => observer.observe(el));
        };

        observeAll();

        // Sections that mount later (lazy data, accordions) still get revealed.
        const mutations = new MutationObserver(observeAll);
        mutations.observe(root, { childList: true, subtree: true });

        return () => {
            mutations.disconnect();
            observer.disconnect();
            root.classList.remove("ff-reveal-scope");
        };
    }, []);

    return (
        <>
            <GlobalStyles styles={REVEAL_CSS} />
            <div ref={rootRef}>{children}</div>
        </>
    );
};

export default ScrollRevealScope;
